<script lang="ts">
	/**
	 * What this customer did on the storefront, on the support page (§12.10).
	 *
	 * "They say they tried to buy twice and it did not work" is a question support is asked, and
	 * until analytics existed the honest answer was that nothing had been written down. The last few
	 * visits are here rather than a click away because that is how support reads a customer page —
	 * top to bottom, once — and a link alone makes the answer something you have to already know to
	 * go and look for.
	 *
	 * Five, and then the link. The full explorer is the same timeline this drills into, so what is
	 * here is a summary rather than a second rendering of it: everything past the recent few is a
	 * different job — filtering, comparing, reading one visit event by event — and that job has a
	 * screen already.
	 *
	 * **Its own read, separately guarded.** A customer page is opened to answer a question about
	 * money, and the visits are the least of it: a support agent must still see the subscriptions,
	 * the invoices and the chargeback flag when the analytics tables are slow or unreadable. So a
	 * failure here costs this panel and nothing else, exactly as each panel on the operations and
	 * analytics screens is guarded on its own.
	 */

	import {
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import { EmptyState, Panel, RelativeDate, RoutedLink } from '../../../components';
	import { analyticsStageName, analyticsVisitDuration } from '../../../Utilities';
	import { listAnalyticsSessions } from '../../../AppNetwork';
	import type { AnalyticsSessionRow } from '../../../AppTypes';

	/** The **user's** uuid, which is how a customer is addressed everywhere on this screen. */
	export let uuid: string = '';

	/** Enough to recognise a pattern, few enough to stay a summary. */
	const RECENT = 5;

	/**
	 * All of them, not the dashboard's default month.
	 *
	 * The filter's own default is the last thirty days, which is the right window for a report about
	 * the store and the wrong one for a question about a person: a customer who bought in March and
	 * wrote in today would show no visits at all, and the panel would be stating something false
	 * rather than saying it did not look. The server clamps beyond its own ceiling, and the panel
	 * says what it covered.
	 */
	const WINDOW_DAYS = 800;

	let visits: AnalyticsSessionRow[] = [];
	let total = 0;
	let loading = true;
	let loadError = '';

	$: void fnLoad(uuid);

	async function fnLoad(userUUID: string): Promise<void> {
		if (userUUID === '') return;
		loading = true;
		try {
			const from = new Date(Date.now() - WINDOW_DAYS * 86400000).toISOString();
			const page = await listAnalyticsSessions(
				{ userUUID, from, to: new Date().toISOString(), includeBots: true },
				{ limit: RECENT, offset: 0 }
			);
			visits = page.items;
			total = page.total;
			loadError = '';
		} catch (error) {
			loadError = error instanceof Error ? error.message : 'Visits could not be loaded';
			visits = [];
		} finally {
			loading = false;
		}
	}

	$: explorerHref = `/Settings/Admin/SalesFunnel?userUUID=${encodeURIComponent(uuid)}`;
</script>

<Panel title="Visits" faIcon="route" class="mt-6">
	<svelte:fragment slot="titleRight">
		{#if total > 0}
			<span class="text-xs text-default">{total.toLocaleString()} recorded</span>
		{/if}
	</svelte:fragment>

	{#if loadError.length > 0}
		<EmptyState variant="error" title="Visits could not be loaded." description={loadError}>
			<Button size="xs" color="alternative" on:click={() => fnLoad(uuid)}>Try again</Button>
		</EmptyState>
	{:else if loading && visits.length === 0}
		<EmptyState variant="loading" compact />
	{:else if visits.length === 0}
		<!--
			An absence with a reason. A visit is only attributed to a customer from the moment the
			store knew who they were, so a purchase made before collection was switched on has no
			journey behind it — which is a fact about the record rather than about the customer, and
			without saying so this reads as somebody who has never visited the site they bought from.
		-->
		<EmptyState
			variant="empty"
			title="No visits recorded for this customer."
			description="A visit is attached to an account from the moment they sign in, so anything from before this store began collecting — or bought without signing in first — has no journey stored against it."
		/>
	{:else}
		<!--
			A table, and the same columns in the same order as the explorer's own list — a support
			agent who reads this panel and then follows the link should not have to re-learn the
			shape of a visit on the next screen.

			`divClass` rather than a wrapping div, matching the invoice and subscription tables further
			down this same page: a landing path is one long token and the table has to be the thing
			that scrolls sideways, never the page.
		-->
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Started</TableHeadCell>
				<TableHeadCell>Got as far as</TableHeadCell>
				<TableHeadCell>Lasted</TableHeadCell>
				<TableHeadCell>Landed on</TableHeadCell>
				<TableHeadCell class="text-right">Events</TableHeadCell>
			</TableHead>
			<TableBody>
				{#each visits as visit (visit.sessionUUID)}
					<TableBodyRow>
						<TableBodyCell class="dataTable">
							<RelativeDate date={visit.startedAt} />
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{analyticsStageName(visit.maxStage)}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{#if visit.inProgress}
								<span class="text-green-600 dark:text-green-400">still going</span>
							{:else}
								{analyticsVisitDuration(visit.durationSeconds)}
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">{visit.entryPath ?? '—'}</TableBodyCell>
						<!-- `tabular-nums` for the alignment, and because it is what opts a number
						     column out of the wrap-anywhere rule that would otherwise break "24"
						     across two lines in a narrow column. -->
						<TableBodyCell class="dataTable text-right tabular-nums">
							{visit.eventCount}
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
	{/if}

	<p class="text-default text-xs mt-3">
		<RoutedLink to={explorerHref} class="text-primary-500 hover:underline">
			Open this customer's visits in the analytics explorer
		</RoutedLink>
		{#if total > visits.length}
			— {(total - visits.length).toLocaleString()} more than shown here.
		{/if}
	</p>
</Panel>
